import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  "How to set a realistic fundraising goal for your sports season — how to add up the real costs, split it into a monthly number, and adjust as the season goes.";

const FAQS = [
  {
    q: 'How do I figure out my fundraising goal number?',
    a: "Add up the season's real costs — registration, travel and lodging, gear, tournament or meet fees — and subtract what your family already covers. Families spend an average of $1,016 per child on their primary sport per year (Aspen Institute's Project Play survey), so use that as a sanity check, not a rule; sports like ice hockey or elite travel teams run well above it.",
  },
  {
    q: 'Should I set a stretch goal or a conservative one?',
    a: "Set the real number, not a stretch number. An honest goal ($1,200 for the season, say) is easier for supporters to believe and easier for you to hit — and hitting your goal builds momentum for next season. A goal that's obviously padded reads as less trustworthy.",
  },
  {
    q: 'Should my goal be a one-time total or a monthly number?',
    a: "Both, but lead with monthly. A single $1,200 target is a big ask; \"$15–$20 a month\" from ten to fifteen supporters reaches the same total and is a far easier yes for any individual supporter — and it keeps money coming in as costs land through the season instead of needing it all up front.",
  },
  {
    q: "What if I don't reach my goal?",
    a: "Fundraising doesn't stop at the goal line — supporters can keep giving after you hit it, and a goal you fall short of isn't wasted; every dollar still goes straight to your season. If you're well short partway through, it's a signal to widen who you're asking, not to give up on the number.",
  },
  {
    q: 'Should I show my goal publicly on my fund page?',
    a: "Yes, generally. A visible goal and progress bar gives supporters context for how much to give and shows momentum, which itself encourages more giving. If a specific number feels too exposing, a general target range works too.",
  },
];

const HowToSetAFundraisingGoal: React.FC = () => {
  usePageMeta(
    'How to Set a Fundraising Goal for Your Sports Season',
    ARTICLE_DESCRIPTION,
    {
      article: true,
      image: '/resources/athlete-training.jpg',
      breadcrumbs: [
        { name: 'Resources', path: '/resources' },
        { name: 'How to Set a Fundraising Goal' },
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    }
  );
  const TOC_SECTIONS = [
    {
      id: "1-add-up-the-real-costs",
      text: "1. Add up the real costs"
    },
    {
      id: "2-subtract-what-s-already-covered",
      text: "2. Subtract what's already covered"
    },
    {
      id: "3-turn-the-total-into-a-monthly-number",
      text: "3. Turn the total into a monthly number"
    },
    {
      id: "4-round-to-a-number-you-can-say-out-loud",
      text: "4. Round to a number you can say out loud"
    },
    {
      id: "5-make-progress-visible",
      text: "5. Make progress visible"
    },
    {
      id: "6-revisit-it-mid-season",
      text: "6. Revisit it mid-season"
    },
    {
      id: "where-sport-fund-fits",
      text: "Where Sport Fund fits"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        How to set a fundraising goal for your sports season (2026)
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        A goal that's too vague gets ignored, and one that's obviously padded loses trust. Here's how to land on a
        number that's real — and easy for supporters to say yes to.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          "Support my season" isn't a goal — it's a mood. Supporters give more, and more confidently, when they can
          see exactly what their gift is going toward and how far it goes. Setting a real number takes fifteen
          minutes and it's worth doing before you send a single message.
        </p>

        <h2 id="1-add-up-the-real-costs">1. Add up the real costs</h2>
        <p>
          Start with everything you can actually list: registration or club dues, travel and lodging, gear and
          equipment, tournament or meet entry fees. Families spend an average of <strong>$1,016 per child</strong>{' '}
          on their primary sport per year, according to the Aspen Institute's Project Play survey — soccer and
          basketball tend to run closer to <strong>$1,000–$1,200</strong>, while travel-heavy sports like ice hockey
          average <strong>$2,500+</strong> and elite club or travel teams can run <strong>$5,000–$20,000+</strong>.
          Use those as a sanity check against your own list, not a substitute for it — every season's costs are
          different.
        </p>

        <h2 id="2-subtract-what-s-already-covered">2. Subtract what's already covered</h2>
        <p>
          Whatever your family is already paying out of pocket doesn't need to be part of the ask. Your goal should
          be the gap — the amount you actually need supporters to help close, not the full season cost.
        </p>

        <h2 id="3-turn-the-total-into-a-monthly-number">3. Turn the total into a monthly number</h2>
        <p>
          A single lump-sum ask is a big decision for most supporters. Divide your total across the season and
          across your expected number of supporters, and lead with that instead: ten supporters giving{' '}
          <strong>$15–$20 a month</strong> for a five-month season covers <strong>$750–$1,000</strong> — close to a
          full season's costs for many sports — without anyone having to write one big check.
        </p>

        <h2 id="4-round-to-a-number-you-can-say-out-loud">4. Round to a number you can say out loud</h2>
        <p>
          $1,200 is easier to hold in your head — and easier for a supporter to picture reaching — than $1,187.42.
          Round to something clean, and don't inflate it. An honest, specific number builds more trust than a padded
          one, and it's the number you'll actually explain to people who ask.
        </p>

        <h2 id="5-make-progress-visible">5. Make progress visible</h2>
        <p>
          Once your goal is set, showing progress toward it does real work: it gives new supporters a sense of
          momentum, and it gives existing supporters a reason to check back in. A visible goal on your fund page
          does this automatically, without you having to post an update every time someone gives.
        </p>

        <h2 id="6-revisit-it-mid-season">6. Revisit it mid-season</h2>
        <p>
          If costs change — a new travel tournament gets added, gear needs replacing — it's fine to adjust the goal
          and say why. Supporters respond well to a specific reason ("we just qualified for regionals, which adds
          travel") far better than a goal that quietly moves with no explanation.
        </p>

        <h2 id="where-sport-fund-fits">Where Sport Fund fits</h2>
        <p>
          A Sport Fund page is built to carry this goal for you: set a target, and supporters see live progress
          toward it every time they visit. Recurring monthly giving is built in, so the "$15–$20 a month" number
          from step 3 is a one-click setup for the person reading your ask, not something they have to remember to
          repeat.
        </p>
        <p>
          <Link to="/signup-creator" className="text-blue-600">
            Start a free fund page
          </Link>{' '}
          and set your goal in a couple of minutes.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/cost-of-youth-sports-2026" className="text-blue-600">
            How Much Does a Season of Youth Sports Really Cost in 2026?
          </Link>{' '}
          — the full breakdown by sport behind the numbers above.
        </p>
        <p>
          <Link to="/resources/how-to-fundraise-for-a-sports-season" className="text-blue-600">
            How to Fundraise for Your Sports Season: 7 Honest Ways That Actually Work
          </Link>{' '}
          — once your goal is set, here's how to actually reach it.
        </p>
        <ArticleFeedback articleSlug="how-to-set-a-fundraising-goal" />
      </article>
    </div>
  );
};

export default HowToSetAFundraisingGoal;
